import { SchemaDataModel } from './SchemaEntityRelations/types';
import {
  FieldType,
  IColumnDefinitionMeta,
  IColumnDefinitionMetaFieldsFragment,
  ICustomColumnDefinitionInput,
  IDataModelAddDimensionInput,
  IDataModelAddFactTableInput,
  IDataModelAddFieldInput,
  IDataModelEditTableInput,
  IDataModelFieldTypeInput,
  IDataModelJoinInput,
  IDataModelJoinTemporalConditionInput,
  IDataModelTemporalColumnsInput,
  IDataModelTemporalFieldRefFieldsFragment,
  IDataModelTemporalFieldRefsFieldsFragment,
  IFieldTypeParamsFieldsFragment,
  IFieldTypeParamsInput,
  IMapDefinitionInput,
  IMapDefinitionMetaFieldsFragment,
  IPreviewColumnDataFieldsFragment,
  ISchemaDataModelFieldsFragment,
  ISchemaFieldMetaFieldsFragment,
  ISchemaFieldsFragment,
  IStructDefinitionInput,
  IStructDefinitionMetaFieldsFragment,
  ITableColumnFieldsFragment,
  ITableJoinPairMeta,
  ITableMetaFieldsFragment,
  JoinConditionOperator,
  Order,
  PermissionKey,
  TemporalJoinType,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import {
  dataModelerAddDraftFunnelPartitionField,
  dataModelerRemoveDraftFunnelPartitionField,
  dataModelerUpdateDraftFunnelPartitionName,
  IAddedDimensionDraftData,
  IAddedFactDraftData,
  IDataModelerDraftEdits,
  IDataModelerDraftFieldData,
  IDataModelerSchemaState,
  IDerivedColumnDraft,
  IEditedTableDraftData,
  IFieldTypeConfig,
  IJoinDraftData,
  ITemporalColumnRefDraftData,
  ITemporalJoinConditionDraftData,
  ITemporalTableColumnsDraftData,
} from '../../../redux/slices/dataModeler';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../redux/selectors/dataModeler';
import { defaultEmptyDraftEdits } from '../../../redux/slices/dataModeler/dataModelerSlice';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import {
  useBootstrapSelector,
  useUserSettingsSelector,
} from '../../../redux/selectorHooks';
import { freeze } from '@reduxjs/toolkit';
import { ICdwTableColumnData } from './DataModelerCdwTableGrid/types';
import { z } from 'zod';
import { getFunnelPartitionNameValidator } from './validation';
import { DATA_MODEL_FIELD_TYPE_CONFIG } from '../../../redux/slices/dataModeler/utils';
import { JOIN_CONDITION_REL_OP_CONFIG } from './DataModelerJoin/utils';
import { UnreachableCaseError } from 'ts-essentials';
import { MaybeNil } from '../../../utils/interfaces';

export const getViewTables = (
  viewTableKeys: string[],
  allTables: ITableMetaFieldsFragment[],
) => {
  return allTables.filter((t) => viewTableKeys.includes(t.key));
};

export const getTableFromKey = (
  tableKey: string,
  dataModel: ISchemaDataModelFieldsFragment,
) => {
  const ret = dataModel.tables.find((tm) => tm.key === tableKey);
  assertNotNil(ret);
  return ret;
};

const getAllModelFieldsInternal = (dataModel: MaybeNil<SchemaDataModel>) =>
  dataModel
    ? [
        ...dataModel.fact.fields,
        ...dataModel.dimensions.flatMap((d) => d.fields),
        ...dataModel.derivedFields,
      ]
    : [];

export const getAllModelFields = (dataModel: MaybeNil<SchemaDataModel>) =>
  getAllModelFieldsInternal(dataModel).sort((a, b) => (a.key > b.key ? 1 : -1));

export const getViewModelFields = (
  dataModel: SchemaDataModel,
  viewKey: string,
): ISchemaFieldMetaFieldsFragment[] =>
  dataModel.fact.key === viewKey
    ? dataModel.fact.fields
    : dataModel.dimensions.find((dim) => dim.key === viewKey)?.fields ?? [];

export const getModelField = (
  dataModel: MaybeNil<SchemaDataModel>,
  fieldKey: string,
) => {
  return getAllModelFieldsInternal(dataModel).find(
    (fieldMeta) => fieldMeta.key === fieldKey,
  );
};

interface ISearchableObject {
  key?: string | null;
  name?: string | null;
  description?: string | null;
  column?: string | null;
  viewColumn?: string | null;
}

export const matchLowerCaseNeedleStr = (
  needle: string,
  haystack?: string | null,
) =>
  needle === ''
    ? true
    : haystack
      ? haystack.toLowerCase().includes(needle)
      : false;

export const dataModelerSearchMatches = (
  term: string,
  obj?: ISearchableObject,
): boolean => {
  const lcTerm = term.trim().toLowerCase();
  return (
    !!obj &&
    (matchLowerCaseNeedleStr(lcTerm, obj.key) ||
      matchLowerCaseNeedleStr(lcTerm, obj.name) ||
      matchLowerCaseNeedleStr(lcTerm, obj.description) ||
      matchLowerCaseNeedleStr(lcTerm, obj.column) ||
      matchLowerCaseNeedleStr(lcTerm, obj.viewColumn))
  );
};

// a helper to produce a default IFieldTypeParamsFieldsFragment with possibly
// customized subset fo the props if params is provided
export const defaultFieldTypeParams = (
  params?: Partial<IFieldTypeParamsFieldsFragment>,
): IFieldTypeParamsFieldsFragment => {
  return {
    fixedTz: params?.fixedTz ?? null,
    linearDistribution: params?.linearDistribution ?? false,
    arrayItemType: params?.arrayItemType ?? null,
    customDefinition: params?.customDefinition ?? null,
    structDefinition: params?.structDefinition ?? null,
    mapDefinition: params?.mapDefinition ?? null,
    __typename: 'FieldTypeParams',
  };
};

export const defaultFieldTypeParamsInput = (
  params?: Partial<IFieldTypeParamsInput>,
): IFieldTypeParamsInput => {
  return {
    fixedTz: params?.fixedTz ?? null,
    linearDistribution: params?.linearDistribution ?? false,
    arrayItemType: params?.arrayItemType ?? null,
    customDefinition: params?.customDefinition ?? null,
    structDefinition: params?.structDefinition ?? null,
    mapDefinition: params?.mapDefinition ?? null,
  };
};

export const emptyFieldTypeParams = freeze(defaultFieldTypeParams());

export const emptyCustomColumnDraft: IDerivedColumnDraft = freeze({
  columnName: '',
  columnNameError: 'Please enter column name',
  fieldType: FieldType.String,
  typeParams: emptyFieldTypeParams,
  originalTypeParams: null,
  definitionType: null,
  definition: null,
  isPublished: false,
});

const structDefinitionInputToMeta = (
  input: IStructDefinitionInput | null,
): IStructDefinitionMetaFieldsFragment | null => {
  if (input === null) {
    return null;
  }
  return {
    __typename: 'StructDefinitionMeta',
    fields: input.fields.map((fldInput) => {
      return {
        __typename: 'StructFieldMeta',
        type: fldInput.type,
        name: fldInput.name,
      };
    }),
  };
};

const mapDefinitionInputToMeta = (
  input: IMapDefinitionInput | null,
): IMapDefinitionMetaFieldsFragment | null => {
  if (input === null) {
    return null;
  }
  return {
    __typename: 'MapDefinitionMeta',
    keyType: input.keyType,
    valueType: input.valueType,
  };
};

const customDefinitionInputToMeta = (
  column: string,
  input: ICustomColumnDefinitionInput | null,
): IColumnDefinitionMetaFieldsFragment | null => {
  if (input?.rawSql) {
    return {
      __typename: 'RawSqlDefinitionMeta',
      column,
      rawSql: input.rawSql.rawSql,
      fullDataRequired: input.rawSql.fullDataRequired,
    };
  }
  if (input?.mappedString) {
    return {
      __typename: 'MappedStringDefinitionMeta',
      column,
      mappedColumn: input.mappedString.mappedColumn,
      mapElse: input.mappedString.mapElse,
      mapNull: input.mappedString.mapNull,
      values: input.mappedString.values.map((mapValue) => {
        return {
          __typename: 'MappedValuePair',
          ...mapValue,
        };
      }),
    };
  }
  return null;
};

const tableColumnFromColumnAndTypeInput = (
  column: string,
  typeInput: IDataModelFieldTypeInput,
): ITableColumnFieldsFragment => {
  return {
    __typename: 'TableColumn',
    column,
    type: typeInput.type,
    description: null,
    typeParams: {
      __typename: 'FieldTypeParams',
      fixedTz: typeInput.typeParams?.fixedTz ?? null,
      arrayItemType: typeInput.typeParams?.arrayItemType ?? null,
      linearDistribution: typeInput.typeParams?.linearDistribution ?? false,
      customDefinition: customDefinitionInputToMeta(
        column,
        typeInput.typeParams?.customDefinition ?? null,
      ),
      structDefinition: structDefinitionInputToMeta(
        typeInput.typeParams?.structDefinition ?? null,
      ),
      mapDefinition: mapDefinitionInputToMeta(
        typeInput.typeParams?.mapDefinition ?? null,
      ),
    },
  };
};

export const getDraftCustomColumns = (
  viewKey: string,
  tableKey: string,
  draftFields: IDataModelerDraftFieldData[],
) => {
  const addedCustomColumns: ITableColumnFieldsFragment[] = [];
  draftFields.forEach(({ addInput, extendInput }) => {
    if (
      addInput?.viewKey === viewKey &&
      addInput.tableKey === tableKey &&
      !!addInput.typeInput?.typeParams?.customDefinition
    ) {
      addedCustomColumns.push(
        tableColumnFromColumnAndTypeInput(
          addInput.column,
          addInput.typeInput!!,
        ),
      );
    }
    if (
      extendInput?.tableKey === tableKey &&
      !!extendInput.typeInput?.typeParams?.customDefinition
    ) {
      addedCustomColumns.push(
        tableColumnFromColumnAndTypeInput(
          extendInput.column,
          extendInput.typeInput!!,
        ),
      );
    }
  });
  return addedCustomColumns;
};

export const temporalJoinTypeConfig: Record<
  TemporalJoinType,
  {
    firstLast: string;
    beforeAfter: string;
    numOps: 0 | 1 | 2;
  }
> = {
  FIRST_AFTER: { beforeAfter: 'After', firstLast: 'First', numOps: 1 },
  FIRST_AT_OR_AFTER: {
    beforeAfter: 'At Or After',
    firstLast: 'First',
    numOps: 1,
  },
  FIRST_AT_OR_BEFORE: {
    beforeAfter: 'At Or Before',
    firstLast: 'First',
    numOps: 1,
  },
  FIRST_BEFORE: { beforeAfter: 'Before', firstLast: 'First', numOps: 1 },
  FIRST_BETWEEN: { beforeAfter: 'Between', firstLast: 'First', numOps: 2 },
  FIRST_EVER: { beforeAfter: 'Ever', firstLast: 'First', numOps: 0 },
  LAST_AFTER: { beforeAfter: 'After', firstLast: 'Last', numOps: 1 },
  LAST_AT_OR_AFTER: {
    beforeAfter: 'At Or After',
    firstLast: 'Last',
    numOps: 1,
  },
  LAST_AT_OR_BEFORE: {
    beforeAfter: 'At Or Before',
    firstLast: 'Last',
    numOps: 1,
  },
  LAST_BEFORE: { beforeAfter: 'Before', firstLast: 'Last', numOps: 1 },
  LAST_BETWEEN: { beforeAfter: 'Between', firstLast: 'Last', numOps: 2 },
  LAST_EVER: { beforeAfter: 'Ever', firstLast: 'Last', numOps: 0 },
};

export const getTemporalConditionStr = (
  cond: {
    temporalJoinType: TemporalJoinType;
    dimTemporalKeys: string[];
    factTemporalKey: string | null;
  } | null,
) => {
  if (cond === null) {
    return null;
  }
  const { firstLast, beforeAfter, numOps } =
    temporalJoinTypeConfig[cond.temporalJoinType];
  switch (numOps) {
    case 0: // ever
      return `${firstLast} ${beforeAfter} ordered by ${cond.dimTemporalKeys[0]}`;
    case 1: // [at or] before/after
      return `${firstLast} where ${cond.dimTemporalKeys[0]} is ${beforeAfter} ${cond.factTemporalKey}`;
    case 2: // between
      return `${firstLast} where ${
        cond.factTemporalKey
      } is ${beforeAfter} ${cond.dimTemporalKeys.join(' AND ')}`;
    default:
      throw new UnreachableCaseError(numOps);
  }
};

const getJoinPairStr = (
  pairs: { factKey: string; dimColumn: string; relOp: JoinConditionOperator }[],
) =>
  pairs
    .map(
      (pair) =>
        `fact.${pair.factKey} ${JOIN_CONDITION_REL_OP_CONFIG[pair.relOp].display} ${pair.dimColumn}`,
    )
    .join(' AND ');

export const getJoinPairMetasStr = (joinPairs: ITableJoinPairMeta[]): string =>
  getJoinPairStr(
    joinPairs.map((pair) => {
      return {
        factKey: pair.factFieldKey,
        dimColumn: pair.dimTableFieldColumn,
        relOp: pair.relOp,
      };
    }),
  );

const getDimensionFieldKeyFromTableColumn = (
  viewKey: string,
  tableColumn: string,
  schemaObj: ISchemaDataModelFieldsFragment,
  addedFields: IDataModelAddFieldInput[],
) =>
  schemaObj.dimensions
    .find((dim) => dim.key === viewKey)
    ?.fields?.find((fld) => fld.tableFields[0].column === tableColumn)?.key ??
  addedFields.find(
    (addedFieldInput) =>
      addedFieldInput.viewKey === viewKey &&
      addedFieldInput.column === tableColumn,
  )?.key ??
  null;

const temporalConditionDataToInput = (
  viewKey: string,
  temporalCondition: ITemporalJoinConditionDraftData,
  schemaObj: ISchemaDataModelFieldsFragment,
  addedFields: IDataModelAddFieldInput[],
): IDataModelJoinTemporalConditionInput => {
  const {
    factTemporalKey,
    dimTemporalColumn,
    dimSecondTemporalColumn,
    joinType,
  } = temporalCondition;
  assertNotNil(dimTemporalColumn);
  const dimTemporalKey = getDimensionFieldKeyFromTableColumn(
    viewKey,
    dimTemporalColumn,
    schemaObj,
    addedFields,
  );
  assertNotNil(dimTemporalKey); // this would fail server side otherwise
  const dimSecondTemporalKey = dimSecondTemporalColumn
    ? getDimensionFieldKeyFromTableColumn(
        viewKey,
        dimSecondTemporalColumn,
        schemaObj,
        addedFields,
      )
    : null;
  return {
    joinType,
    factTemporalKey,
    dimTemporalKey,
    dimSecondTemporalKey,
  };
};

export const joinDataToJoinInput = (
  viewKey: string,
  joinData: IJoinDraftData,
  schemaObj: ISchemaDataModelFieldsFragment,
  addedFields: IDataModelAddFieldInput[],
): IDataModelJoinInput => {
  const temporalCondition = joinData.temporalCondition
    ? temporalConditionDataToInput(
        viewKey,
        joinData.temporalCondition,
        schemaObj,
        addedFields,
      )
    : null;
  return {
    conditions: joinData.conditions.map(
      ({ factFieldKey, dimTableColumn, relOp }) => {
        return {
          factFieldKey,
          dimTableColumn,
          relOp,
        };
      },
    ),
    sqlCondition: joinData.sqlCondition,
    temporalCondition,
  };
};

export const dimensionDraftDataToAddedDimensionInput = (
  addedDimensionData: IAddedDimensionDraftData,
  schemaObj: ISchemaDataModelFieldsFragment,
  addedFields: IDataModelAddFieldInput[],
): IDataModelAddDimensionInput => {
  const { viewKey, tableKey, sqlTableName, joinData, installTemporals } =
    addedDimensionData;
  const join = joinDataToJoinInput(viewKey, joinData, schemaObj, addedFields);
  return {
    viewKey,
    tableKey,
    sqlTableName,
    join,
    installTemporals: installTemporals
      ? temporalsDraftDataToInput(installTemporals)
      : null,
  };
};

const temporalsDraftDataToInput = (
  draftData: ITemporalTableColumnsDraftData,
): IDataModelTemporalColumnsInput => {
  return {
    date: draftData.date.column,
    dayOfWeek: draftData.dayOfWeek.column,
    fourWeek: draftData.fourWeek.column,
    hourOfDay: draftData.hourOfDay.column,
    month: draftData.month.column,
    quarter: draftData.quarter.column,
    ts: draftData.ts.column,
    week: draftData.week.column,
    year: draftData.year.column,
  };
};

export const factDraftDataToAddedFactInput = (
  addedFactData: IAddedFactDraftData,
): IDataModelAddFactTableInput => {
  const { tableKey, sqlTableName, temporals, installTemporals } = addedFactData;
  return {
    tableKey,
    sqlTableName,
    eventTemporals: temporalsDraftDataToInput(temporals),
    installTemporals: installTemporals
      ? temporalsDraftDataToInput(installTemporals)
      : null,
  };
};

export const editTableDraftDataToEditTableInput = (
  editTableData: IEditedTableDraftData,
): IDataModelEditTableInput => {
  const { viewKey, tableKey, installTemporals } = editTableData;
  return {
    viewKey,
    tableKey,
    installTemporals: installTemporals
      ? temporalsDraftDataToInput(installTemporals)
      : null,
    sqlTableName: null, // TODO support table renames by SEs
  };
};

export const generateSortableColumnClickFunc = <TKeys>(
  forSortColumn: TKeys,
  sortColumn: TKeys,
  setSortColumn: (sortColumn: TKeys) => void,
  setSortOrderOrNull: (func: (prev: Order | null) => Order | null) => void,
) => {
  return () => {
    const sameColumn = forSortColumn === sortColumn;
    setSortColumn(forSortColumn);
    if (sameColumn) {
      // default is a special 3-state condition
      if (forSortColumn === 'default') {
        setSortOrderOrNull((prev) =>
          prev === null ? Order.Asc : prev === Order.Desc ? null : Order.Desc,
        );
      } else {
        setSortOrderOrNull((prev) =>
          prev === Order.Asc ? Order.Desc : Order.Asc,
        );
      }
    } else {
      setSortOrderOrNull((_) => Order.Asc);
    }
  };
};

export const listToHashMapSkipNulls = <T>(
  list: T[],
  getKey: (item: T) => string | null,
): {
  [key: string]: T | undefined;
} => {
  const ret: {
    [key: string]: T | undefined;
  } = {};
  list.forEach((item) => {
    const k = getKey(item);
    if (k) {
      ret[k] = item;
    }
  });
  return ret;
};

export const listToHashMapFromKey = <T>(
  list: T[],
  key: keyof T,
): {
  [key: string]: T | undefined;
} => listToHashMapSkipNulls(list, (item) => item[key] as string);

const temporalColumnDraftDataForTable = (
  ref: IDataModelTemporalFieldRefFieldsFragment | null,
  tableKey: string,
): ITemporalColumnRefDraftData => {
  return (
    ref?.columnRefs?.find((colRef) => colRef.tableKey === tableKey) ?? {
      column: null,
      isAutoGenerated: true,
    }
  );
};

export const temporalFieldRefsToTableColumnsDraftData = (
  temporalRefs: IDataModelTemporalFieldRefsFieldsFragment,
  forTableKey: string,
): ITemporalTableColumnsDraftData => {
  return {
    date: temporalColumnDraftDataForTable(temporalRefs.date, forTableKey),
    dayOfWeek: temporalColumnDraftDataForTable(
      temporalRefs.dayOfWeek,
      forTableKey,
    ),
    fourWeek: temporalColumnDraftDataForTable(
      temporalRefs.fourWeek,
      forTableKey,
    ),
    hourOfDay: temporalColumnDraftDataForTable(
      temporalRefs.hourOfDay,
      forTableKey,
    ),
    month: temporalColumnDraftDataForTable(temporalRefs.month, forTableKey),
    quarter: temporalColumnDraftDataForTable(temporalRefs.quarter, forTableKey),
    ts: temporalColumnDraftDataForTable(temporalRefs.ts, forTableKey),
    week: temporalColumnDraftDataForTable(temporalRefs.week, forTableKey),
    year: temporalColumnDraftDataForTable(temporalRefs.year, forTableKey),
  };
};

export type TemporalFieldKey = keyof ITemporalTableColumnsDraftData;

export interface TemporalFieldKeyConfig {
  temporalKey: TemporalFieldKey;
  requiredType: FieldType;
}

export const temporalFieldKeysConfig: TemporalFieldKeyConfig[] = [
  {
    temporalKey: 'ts',
    requiredType: FieldType.Timestamp,
  },
  {
    temporalKey: 'hourOfDay',
    requiredType: FieldType.Integer,
  },
  {
    temporalKey: 'date',
    requiredType: FieldType.Date,
  },
  {
    temporalKey: 'dayOfWeek',
    requiredType: FieldType.Integer,
  },
  {
    temporalKey: 'week',
    requiredType: FieldType.Date,
  },
  {
    temporalKey: 'fourWeek',
    requiredType: FieldType.Date,
  },
  {
    temporalKey: 'month',
    requiredType: FieldType.Date,
  },
  {
    temporalKey: 'quarter',
    requiredType: FieldType.Date,
  },
  {
    temporalKey: 'year',
    requiredType: FieldType.Date,
  },
];

export const useAvailableFieldsToExtend = ({
  tableKey,
  filterTypes,
  columnData,
}: {
  tableKey: string;
  filterTypes: FieldType[];
  columnData: ICdwTableColumnData;
}) => {
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  return useMemo(() => {
    const extendsInThisTable = draftEdits.draftFieldsData.flatMap(
      (draftField) =>
        draftField.tableKey === tableKey && !!draftField.extendInput
          ? [draftField.extendInput]
          : [],
    );
    const existingUnmappedInTable = schemaObj.fact.fields.filter(
      (fieldMeta) =>
        !fieldMeta.tableFields.map((tf) => tf.tableKey).includes(tableKey) &&
        !extendsInThisTable.some((extendInput) => {
          return (
            extendInput.fieldKey === fieldMeta.key &&
            extendInput.column !== columnData.column // XXX this column has to remain in the list
          );
        }) &&
        (filterTypes.length === 0 || filterTypes.includes(fieldMeta.type)),
    );
    const addInputsFromOtherTables = draftEdits.draftFieldsData.flatMap(
      (draftField) =>
        draftField.viewKey === schemaObj.fact.key &&
        !!draftField.addInput &&
        draftField.addInput.tableKey !== tableKey
          ? [draftField.addInput]
          : [],
    );

    const addedDraftsUnmappedInTable = addInputsFromOtherTables.filter(
      (addInput) =>
        !extendsInThisTable.some(
          (extendInput) =>
            extendInput.fieldKey === addInput.key &&
            extendInput.column !== columnData.column, // XXX this column has to remain in the list
        ) &&
        (filterTypes.length === 0 ||
          filterTypes.includes(addInput.typeInput.type)),
    );
    return {
      existingUnmappedInTable,
      addedDraftsUnmappedInTable,
    };
  }, [
    draftEdits.draftFieldsData,
    schemaObj.fact.fields,
    schemaObj.fact.key,
    tableKey,
    filterTypes,
    columnData.column,
  ]);
};

const createNewlyCreatedSchemaObjFromState = (
  schemaId: string,
  schemaState: IDataModelerSchemaState,
): ISchemaFieldsFragment => {
  const disabled = schemaState.draftEdits.disabled ?? false;
  return {
    __typename: 'Schema',
    id: schemaId,
    name: schemaState.schemaDataModel?.name ?? schemaId,
    supportsAnyEvent: true,
    latestDataAt: null,
    disabled,
    description: schemaState.draftEdits.description,
    requiredPermission: null,
    isInternal: false,
  };
};

export const useDataModelerPermittedSchemas = (): {
  currentSchemaId: string | null;
  permittedSchemas: ISchemaFieldsFragment[];
} => {
  const urlParams = useParams();
  const defaultUserSchemaId = useUserSettingsSelector('defaultSchemaId');
  const urlSchemaId = urlParams.schemaId;
  const schemaConfig = useBootstrapSelector('schemaConfig');
  assertNotNil(schemaConfig);
  const { allSchemas } = schemaConfig;
  // Data modeler can only edit non internal schemas. I.e internal schemas like Kubit Analytics should be skipped
  const customerSchemas = allSchemas.filter((schema) => !schema.isInternal);
  const newlyCreatedSchemas: ISchemaFieldsFragment[] = useAppSelector(
    (state) => {
      return Object.keys(state.dataModeler.schemaStates).flatMap((schemaId) => {
        // XXX filter out existing schemas
        if (
          customerSchemas.some(
            (existingSchema) => existingSchema.id === schemaId,
          )
        ) {
          return [];
        }
        const schemaState = state.dataModeler.schemaStates[schemaId];
        if (!schemaState) {
          return [];
        }
        return [createNewlyCreatedSchemaObjFromState(schemaId, schemaState)];
      });
    },
  );

  const isNewSchema = newlyCreatedSchemas.some(
    (schema) => schema.id === urlSchemaId,
  );

  const ignoreUrlSchema =
    !customerSchemas.some((schema) => schema.id === urlSchemaId) &&
    !isNewSchema;

  const isDefaultUserSchemaExternal = customerSchemas.some(
    (schema) => schema.id === defaultUserSchemaId,
  );

  const defaultSchemaIdOrNull = ignoreUrlSchema
    ? (isDefaultUserSchemaExternal ? null : defaultUserSchemaId) ??
      customerSchemas[0]?.id ??
      null
    : urlSchemaId ??
      (isDefaultUserSchemaExternal ? null : defaultUserSchemaId) ??
      customerSchemas[0]?.id ??
      null;

  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    // XXX passing an invalid empty string instead of null for defaultSchemaId, and we are prepared
    // to get that back as the selectedSchemaId below. Alternative is to make the param and
    // response selectedSchemaId nullable, but then we will have to assertNotNull the result
    // in all places usePermissionSchemas is used.
    defaultSchemaId: defaultSchemaIdOrNull ?? '',
    requestedPermissionKey: PermissionKey.DataModelView,
  });

  const permittedSchemas: ISchemaFieldsFragment[] =
    permittedSchemaIds && permittedSchemaIds.length > 0
      ? customerSchemas.filter((schemaId) =>
          permittedSchemaIds.includes(schemaId.id),
        )
      : customerSchemas;

  const isSelectedSchemaInternal = customerSchemas.some(
    (schema) => schema.id === selectedSchemaId,
  );

  // XXX selectedSchemaId can be invalid (empty string here), if there is no default schema
  const currentSchemaId =
    isNewSchema && urlSchemaId
      ? urlSchemaId
      : selectedSchemaId === '' || !isSelectedSchemaInternal
        ? null
        : selectedSchemaId;
  return {
    currentSchemaId,
    permittedSchemas: [...newlyCreatedSchemas, ...permittedSchemas],
  };
};

export const autoSchemaKeyFromName = (
  name: string,
  otherSchemaKeys: string[],
): string => {
  const desiredKey = name.toUpperCase().replaceAll(/\W+/g, '_');
  let ret = desiredKey;
  let ix = 0;
  const otherSchemaKeysSet = new Set(otherSchemaKeys);
  while (otherSchemaKeysSet.has(ret)) {
    ix += 1;
    ret = `${desiredKey}_${ix}`;
  }
  return ret;
};

export const useFunnelPartitionModel = ({
  draftEdits,
  schemaObj,
}: {
  draftEdits: MaybeNil<IDataModelerDraftEdits>;
  schemaObj: MaybeNil<ISchemaDataModelFieldsFragment>;
}) => {
  const dispatch = useAppDispatch();
  const existingPartitions: {
    name: string;
    fieldKey: string;
    fieldName: string;
  }[] = useMemo(() => {
    if (!schemaObj?.funnelPartitions) {
      return [];
    }
    return schemaObj.funnelPartitions.flatMap(({ fieldKey, name }) => {
      const field = getModelField(schemaObj, fieldKey);
      return field
        ? [
            {
              fieldKey: field.key,
              fieldName: field.name,
              name,
            },
          ]
        : [];
    });
  }, [schemaObj]);

  const draftPartitions: {
    name: string;
    fieldKey: string;
    fieldName: string;
  }[] = useMemo(() => {
    if (!schemaObj) {
      return [];
    }
    if (!draftEdits?.addFunnelPartitions) {
      return [];
    }
    return draftEdits.addFunnelPartitions.map(({ fieldKey, name }) => {
      const existingField = getModelField(schemaObj, fieldKey);
      if (existingField) {
        return {
          fieldKey: existingField.key,
          fieldName: existingField.name,
          name,
        };
      }
      const draftAddInput = draftEdits.draftFieldsData.find(
        (draft) => draft?.addInput?.key === fieldKey,
      )?.addInput;
      assertNotNil(draftAddInput);
      return {
        fieldKey: draftAddInput.key,
        fieldName: draftAddInput.name ?? draftAddInput.viewColumn,
        name,
      };
    });
  }, [draftEdits?.addFunnelPartitions, draftEdits?.draftFieldsData, schemaObj]);

  const removeDraftPartitionField = useCallback(
    (fieldKey: string) => {
      dispatch(dataModelerRemoveDraftFunnelPartitionField(fieldKey));
    },
    [dispatch],
  );

  const addDraftPartitionField = useCallback(
    (fieldKey: string) => {
      const fieldName =
        getModelField(schemaObj, fieldKey)?.name ??
        draftEdits?.draftFieldsData.find(
          ({ addInput }) => addInput?.key === fieldKey,
        )?.addInput?.name ??
        fieldKey;
      dispatch(
        dataModelerAddDraftFunnelPartitionField({
          fieldKey,
          name: fieldName,
          key: fieldKey,
        }),
      );
    },
    [dispatch, draftEdits?.draftFieldsData, schemaObj],
  );

  const updateDraftPartitionName = useCallback(
    (fieldKey: string, name: string) => {
      dispatch(
        dataModelerUpdateDraftFunnelPartitionName({
          fieldKey,
          name,
        }),
      );
    },
    [dispatch],
  );

  const unavailableFieldKeys = useMemo(
    () => [
      ...existingPartitions.map(({ fieldKey }) => fieldKey),
      ...draftPartitions.map(({ fieldKey }) => fieldKey),
    ],
    [draftPartitions, existingPartitions],
  );

  const partitionNameValidator = useMemo(() => {
    return (currentPartitionFieldKey: string) => {
      const allParts = [...existingPartitions, ...draftPartitions];
      const filteredNames = allParts
        .filter(({ fieldKey }) => fieldKey !== currentPartitionFieldKey)
        .map(({ name }) => name);
      return z.object({
        value: getFunnelPartitionNameValidator(filteredNames),
      });
    };
  }, [existingPartitions, draftPartitions]);

  return useMemo(() => {
    return {
      existingPartitions,
      draftPartitions,
      unavailableFieldKeys,
      removeDraftPartitionField,
      addDraftPartitionField,
      updateDraftPartitionName,
      partitionNameValidator,
    };
  }, [
    addDraftPartitionField,
    draftPartitions,
    existingPartitions,
    partitionNameValidator,
    removeDraftPartitionField,
    unavailableFieldKeys,
    updateDraftPartitionName,
  ]);
};

export const fieldTypeName = (fieldType: FieldType) =>
  DATA_MODEL_FIELD_TYPE_CONFIG[fieldType].humanName;

export const fieldTypesToSelectOptions = (
  list: FieldType[],
): {
  text: string;
  value: string;
}[] =>
  list.map((fieldType) => {
    return {
      text: fieldTypeName(fieldType),
      value: fieldType,
    };
  });

const filterTypes = (filterBy: keyof IFieldTypeConfig) => {
  return Object.keys(DATA_MODEL_FIELD_TYPE_CONFIG).flatMap((fieldTypeKey) => {
    const fieldType = fieldTypeKey as FieldType;
    const conf = DATA_MODEL_FIELD_TYPE_CONFIG[fieldType];
    if (!!conf[filterBy]) {
      return [fieldType];
    } else {
      return [];
    }
  });
};

export const FIELD_TYPE_OPTIONS = fieldTypesToSelectOptions(
  filterTypes('canDefine'),
);

// not all FieldTypes can be array member types
export const ARRAY_ITEM_FIELD_TYPE_OPTIONS = fieldTypesToSelectOptions(
  filterTypes('canBeArrayElement'),
);

export interface IPreviewDataHeader {
  name: string;
  type: FieldType;
}

interface IPreviewDataRow {
  values: string[];
}

export interface IPreviewDataRows {
  header: IPreviewDataHeader[];
  rows: IPreviewDataRow[];
  errors: string[];
  sqlQuery: string | null;
}

export const convertPreviewColumnDataToRows = (
  columnsData: IPreviewColumnDataFieldsFragment[],
  errors: string[],
  sqlQuery: string | null,
  limit: number,
): IPreviewDataRows => {
  if (columnsData.length === 0) {
    return {
      header: [],
      rows: [],
      errors,
      sqlQuery,
    };
  }
  const header = columnsData.map((previewColumn) => {
    return {
      name: previewColumn.column,
      type: previewColumn.type,
    };
  });
  // we request much more rows hoping to get unique values, try deduping here
  const rowsWithDups: IPreviewDataRow[] = columnsData[0].values.map(
    (_, rowNum) => {
      const values = columnsData.map((_, colIx) => {
        return columnsData[colIx].values[rowNum] ?? 'NULL';
      });
      return {
        values,
      };
    },
  );
  const found = new Set<string>();
  const rows: IPreviewDataRow[] = [];
  let rowIx = 0;
  while (rows.length < limit && rowIx < rowsWithDups.length) {
    const row = rowsWithDups[rowIx];
    const rowKey = row.values.join('-');
    if (!found.has(rowKey)) {
      found.add(rowKey);
      rows.push(row);
    }
    rowIx++;
  }
  // now fill up with dups until our limit, but not more than what we already have in rowsWithDups
  while (rows.length < limit && rows.length < rowsWithDups.length) {
    rows.push(rowsWithDups[rows.length]);
  }
  return {
    header,
    rows,
    errors,
    sqlQuery,
  };
};

export const getColumnDefinitionMetaDisplay = (
  definition: IColumnDefinitionMeta,
) => {
  let typeText = '';
  let details = '';
  const { __typename } = definition;
  switch (__typename) {
    case 'RawSqlDefinitionMeta':
      typeText = 'Custom SQL';
      details = definition.rawSql;
      break;
    case 'MappedStringDefinitionMeta':
      typeText = 'Mapped String';
      details =
        `Map values from ${definition.mappedColumn}: ` +
        definition.values
          .map(({ original, mapped }) => `${original} to ${mapped}`)
          .join(', ') +
        (definition.mapNull ? `, NULL to ${definition.mapNull}` : '') +
        (definition.mapElse ? `, else ${definition.mapNull}` : '');
      break;
    default:
      throw new UnreachableCaseError(__typename);
  }
  return {
    typeText,
    details,
  };
};

export const shortTableKey = (schemaKey: string, tableKey: string) => {
  const schemaKeyPrefix = `${schemaKey}.`;
  return tableKey.startsWith(schemaKeyPrefix)
    ? tableKey.substring(schemaKeyPrefix.length)
    : tableKey;
};
